import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { GoogleMap } from '@angular/google-maps';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";

import { SpinnerService } from '../../services/spinner.service';
import { ApiExhibit, ApiSetExhibitsRequest, ApiFile, SettingFile } from '../../api-client-module';
import { ExhibitAttribute } from '../../firestore-models/exhibit-attribute';
import { UploadedFile } from '../../firestore-models/uploaded-file';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ExhibitDetail } from '../../firestore-models/exhibit-detail';
import { ReservationFacility } from '../../firestore-models/reservation-facility';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { MultiNameDialogComponent } from '../../common/multi-name-dialog/multi-name-dialog.component';
import { MultiDescDialogComponent } from '../../common/multi-desc-dialog/multi-desc-dialog.component';
import { MultiYoutubeDialogComponent } from '../../common/multi-youtube-dialog/multi-youtube-dialog.component';
import { MultiExternallinkDialogComponent } from '../../common/multi-externallink-dialog/multi-externallink-dialog.component';
import { SelectLogoFileDialogComponent } from '../../select-logo-file-dialog/select-logo-file-dialog.component';
import { SelectUploadedFileDialogComponent } from '../../select-uploaded-file-dialog/select-uploaded-file-dialog.component';
import { SelectExhibitDetailsDialogComponent } from '../select-exhibit-details-dialog/select-exhibit-details-dialog.component';
import { SelectReservationFacilitiesDialogComponent } from '../select-reservation-facilities-dialog/select-reservation-facilities-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface ExhibitDialogData {
  change_type: number;
  exhibit?: ApiExhibit;
}

export interface marker {
  lat: number;
  lng: number;
  draggable: boolean;
}

@Component({
  selector: 'app-exhibit-dialog',
  templateUrl: './exhibit-dialog.component.html',
  styleUrls: ['./exhibit-dialog.component.css'],
  standalone: false,
})
export class ExhibitDialogComponent implements OnInit {

  //展示情報
  exhibit?: ApiExhibit;
  //各展示情報に紐付くプロジェクトID
  projectId: string;

  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;

  //展示情報フォーム
  exhibitFormGroup!: FormGroup;
  exhibitIdControl!: FormControl;
  exhibitAttributeIdControl!: FormControl;
  autoTransContral!: FormControl;
  exhibitNamesControl!: FormControl;
  exhibitDescsControl!: FormControl;
  exhibitLatControl!: FormControl;
  exhibitLngControl!: FormControl;
  showMarkerControl!: FormControl;
  youtubeCodesControl!: FormControl;
  useYoutubeControl!: FormControl;
  externallinkNamesControl!: FormControl;
  externallinkUrlsControl!: FormControl;
  useExternallinkControl!: FormControl;
  notOverlapGuideControl!: FormControl;
  inExhibitId = '';

  private exhibitAttributesCollection!: AngularFirestoreCollection<ExhibitAttribute>;
  exhibitAttributes!: Observable<ExhibitAttribute[]>;

  exhibitFiles: SettingFile[] = [];
  exhibitDetails: ExhibitDetail[] = [];
  reservationFacilities: ReservationFacility[] = [];

  //GoogleMap表示
  zoom = AppConst.ZOOM_INITIAL_VALUE;
  center!: google.maps.LatLngLiteral;
  options: google.maps.MapOptions = {
    disableDefaultUI: true
  };
  currentPosition!: google.maps.LatLngLiteral
  currentPositionMarkerOption: google.maps.MarkerOptions = {
    draggable: true
  };

  currentLang: string = 'ja';
  projectBaseLang: string = 'ja';

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild("mapSearchField") searchField!: ElementRef;
  @ViewChild("mapSearchFieldExecute") searchFieldExecute!: ElementRef;
  @ViewChild("mapSearchFieldClear") searchFieldClear!: ElementRef;
  searchBox!: google.maps.places.SearchBox;

  // ユーザータイプ
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;
  userType!: number;
  isEdit: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ExhibitDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExhibitDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.userType = this.configurationService.getUserType();
    this.isEdit = this.configurationService.getIsEdit();
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.exhibitAttributesCollection = this.angularFirestore.collection<ExhibitAttribute>(
      AppConst.FIRESTORE_EXHIBIT_ATTR, ref => ref.where('project_id', '==', this.projectId));
    this.exhibitAttributes = this.exhibitAttributesCollection.valueChanges({ idField: 'id' });
    this.currentLang = this.translate.currentLang;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.exhibit = this.data.exhibit;
    if (!this.exhibit) {
      this.exhibit = {
        exhibit_id: '',
        exhibit_attribute_id: '',
        exhibit_names: [],
        exhibit_descs: null as any,
        exhibit_files: [],
        latitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.latitude ? this.configurationService.getProject().project_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
        longitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.longitude ? this.configurationService.getProject().project_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
        show_marker: false,
        youtube_codes: [],
        use_youtube: false,
        externallink_names: [],
        externallink_urls: [],
        use_externallink: false,
        not_overlap_guide: false,
        project_id: this.projectId,
        enabled: true,
      };
      this.isAdded = true;
    } else {
      this.inExhibitId = this.exhibit.exhibit_id;
    }
    this.currentPosition = {
      lat: this.exhibit.latitude!,
      lng: this.exhibit.longitude!
    }
    this.center = this.currentPosition;

    this.createForm();
    this.exhibitIdControl = this.exhibitFormGroup.get('exhibitId') as FormControl;
    this.exhibitAttributeIdControl = this.exhibitFormGroup.get('exhibitAttributeId') as FormControl;
    this.exhibitNamesControl = this.exhibitFormGroup.get('exhibitNames') as FormControl;
    this.exhibitDescsControl = this.exhibitFormGroup.get('exhibitDescs') as FormControl;
    this.exhibitLatControl = this.exhibitFormGroup.get('exhibitLat') as FormControl;
    this.exhibitLngControl = this.exhibitFormGroup.get('exhibitLng') as FormControl;
    this.showMarkerControl = this.exhibitFormGroup.get('showMarker') as FormControl;
    this.youtubeCodesControl = this.exhibitFormGroup.get('youtubeCodes') as FormControl;
    this.useYoutubeControl = this.exhibitFormGroup.get('useYoutube') as FormControl;
    this.externallinkNamesControl = this.exhibitFormGroup.get('externallinkNames') as FormControl;
    this.externallinkUrlsControl = this.exhibitFormGroup.get('externallinkUrls') as FormControl;
    this.useExternallinkControl = this.exhibitFormGroup.get('useExternallink') as FormControl;
    this.notOverlapGuideControl = this.exhibitFormGroup.get('notOverlapGuide') as FormControl;

    this.getFiles();
    this.getExhibitDetails();
    this.getReservationFacilities();
  }

  ngAfterViewInit(): void {
    // 住所検索用
    this.searchBox = new google.maps.places.SearchBox(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldExecute.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldClear.nativeElement
    );
  }

  //GoogleMap上のピンとフォームの値を一致させる
  onMerkerDragEnd(event: any) {
    this.exhibitLatControl.setValue(this.round(event.latLng!.lat(), 8));
    this.exhibitLngControl.setValue(this.round(event.latLng!.lng(), 8));
  }

  //ピンの位置
  private round(value: number, digits: number): number {
    let pow: number = 1;
    if (digits > 0) {
      pow = Math.pow(10, digits);
    }
    return Math.round(value * pow) / pow;
  }

  //Googleマップと緯度のフォーム連携
  onBlurMarkerLat() {
    if (!!this.exhibitLatControl.valid) {
      this.exhibitLatControl.setValue(this.round(this.exhibitLatControl.value, 8));
      this.currentPosition = {
        lat: this.round(this.exhibitLatControl.value, 8),
        lng: this.currentPosition.lng
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //Googleマップと経度のフォーム連携
  onBlurMarkerLng() {
    if (!!this.exhibitLngControl.valid) {
      this.exhibitLngControl.setValue(this.round(this.exhibitLngControl.value, 8));
      this.currentPosition = {
        lat: this.currentPosition.lat,
        lng: this.round(this.exhibitLngControl.value, 8)
      }
      this.map.panTo(this.currentPosition);
    }
  }

  // 展示情報並べ替え
  onDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.exhibitFiles, event.previousIndex, event.currentIndex);
    let files: Array<ApiFile> = [];
    for (const file of this.exhibitFiles) {
      files.push({ file_id: file.id, lang_code: file.lang_code });
    }
    this.exhibit!.exhibit_files = files;
  }

  // 展示詳細情報並べ替え
  onDropExhibitDetail(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.exhibitDetails, event.previousIndex, event.currentIndex);
    let exhibitDetailLists: Array<string> = [];
    for (const exhibitDetail of this.exhibitDetails) {
      exhibitDetailLists.push(exhibitDetail.id!);
    }
    this.exhibit!.exhibit_detail_ids = exhibitDetailLists;
  }

  // 予約設備情報並べ替え
  onDropReservationFacility(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.reservationFacilities, event.previousIndex, event.currentIndex);
    let reservationFacilityLists: Array<string> = [];
    for (const reservationFacility of this.reservationFacilities) {
      reservationFacilityLists.push(reservationFacility.id!);
    }
    this.exhibit!.reservation_facility_ids = reservationFacilityLists;
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToExhibitId() {
    return this.exhibitIdControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.exhibitIdControl.hasError('pattern')
          ? this.translate.instant('半角英数字ハイフンのみ')
          : '';
  }

  public getErrorMessageToExhibitNames() {
    return this.exhibitNamesControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToExhibitDescs() {
    return this.exhibitDescsControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToHallLat() {
    return this.exhibitLatControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.exhibitLatControl.hasError('pattern') || this.exhibitLatControl.hasError('min') || this.exhibitLatControl.hasError('max')
          ? this.translate.instant('-90から90の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToHallLng() {
    return this.exhibitLngControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.exhibitLngControl.hasError('pattern') || this.exhibitLngControl.hasError('min') || this.exhibitLngControl.hasError('max')
          ? this.translate.instant('-180から180の間で小数第８位まで')
          : '';
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.exhibitFormGroup = this.formBuilder.group(
      {
        exhibitId: [this.exhibit!.exhibit_id, [Validators.required, Validators.pattern('^[a-zA-Z0-9-]+$')]],
        exhibitAttributeId: [this.exhibit!.exhibit_attribute_id],
        exhibitNames: [this.exhibit!.exhibit_names, [Validators.required]],
        exhibitDescs: [this.exhibit!.exhibit_descs, [Validators.required]],
        exhibitLat: [this.exhibit!.latitude, [Validators.required, Validators.min(-90), Validators.max(90), Validators.pattern('^(\-)?([1-9][0-9]{0,1}|0)(\.[0-9]{1,8})?$')]],
        exhibitLng: [this.exhibit!.longitude, [Validators.required, Validators.min(-180), Validators.max(180), Validators.pattern('^(\-)?([1-9][0-9]{0,2}|0)(\.[0-9]{1,8})?$')]],
        showMarker: [this.exhibit!.show_marker, []],
        youtubeCodes: [this.exhibit!.youtube_codes, []],
        useYoutube: [this.exhibit!.use_youtube, []],
        externallinkNames: [this.exhibit!.externallink_names, []],
        externallinkUrls: [this.exhibit!.externallink_urls, []],
        useExternallink: [this.exhibit!.use_externallink, []],
        notOverlapGuide: [this.exhibit!.not_overlap_guide, []],
      }
    );
  }

  //展示画像ファイル設定
  private getFiles() {
    this.exhibitFiles = [];
    if (!!this.exhibit!.exhibit_files && this.exhibit!.exhibit_files.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトのアップロードファイルを全て取得してから
      //       展示情報に登録されている id で絞り込む。
      this.angularFirestore.collection<UploadedFile>(
        AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId)
      ).valueChanges({ idField: 'id' }).subscribe(docs => {
        this.exhibitFiles = [];
        if (!!docs) {
          for (const exhibitFile of this.exhibit!.exhibit_files!) {
            let isUploadedFile = false;
            for (const uploadedFile of docs) {
              if (exhibitFile.file_id === uploadedFile.id) {
                const settingFile: SettingFile = {
                  id: uploadedFile.id,
                  file_name: uploadedFile.file_name,
                  download_url: uploadedFile.download_url,
                  lang_code: exhibitFile.lang_code,
                }
                this.exhibitFiles.push(settingFile);
                isUploadedFile = true;
                break;
              }
            }
            // アップロードファイルが見つからない
            if (!isUploadedFile) {
              const settingFile: SettingFile = {
                id: exhibitFile.file_id,
                file_name: "unkhown",
                download_url: "",
                lang_code: exhibitFile.lang_code,
              }
              this.exhibitFiles.push(settingFile);
            }
          }
        }
      });
    }
  }

  // 変更された言語を登録
  changeExhibitFileLang(i: number, event: any) {
    this.exhibit!.exhibit_files![i].lang_code = event.value;
    this.exhibitFiles![i].lang_code = event.value;
  }

  //更新ボタン
  addExhibit(): void {
    if (this.exhibitFormGroup.valid) {
      this.exhibit!.exhibit_id = this.exhibitIdControl.value;
      this.exhibit!.exhibit_attribute_id = this.exhibitAttributeIdControl.value;
      this.exhibit!.exhibit_names = this.exhibitNamesControl.value;
      this.exhibit!.exhibit_descs = this.exhibitDescsControl.value;
      this.exhibit!.latitude = this.exhibitLatControl.value;
      this.exhibit!.longitude = this.exhibitLngControl.value;
      this.exhibit!.show_marker = this.showMarkerControl.value;
      this.exhibit!.youtube_codes = this.youtubeCodesControl.value;
      this.exhibit!.use_youtube = this.useYoutubeControl.value;
      this.exhibit!.externallink_names = this.externallinkNamesControl.value;
      this.exhibit!.externallink_urls = this.externallinkUrlsControl.value;
      this.exhibit!.use_externallink = this.useExternallinkControl.value;
      this.exhibit!.not_overlap_guide = this.notOverlapGuideControl.value;
      if (this.exhibit) {
        let request: ApiSetExhibitsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          exhibits: [this.exhibit]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setExhibits(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }      
    }
  }

  //展示名称ダイアログ
  openExhibitNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.exhibit!.exhibit_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibit!.exhibit_names = result;
        this.exhibitNamesControl.setValue(this.exhibit!.exhibit_names);
      }
    });
  }

  //展示詳細ダイアログ
  openExhibitDescDialog(): void {
    const dialogRef = this.dialog.open(MultiDescDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { descs: this.exhibit!.exhibit_descs }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibit!.exhibit_descs = result;
        this.exhibitDescsControl.setValue(this.exhibit!.exhibit_descs);
      }
    });
  }

  //展示画像ファイル選択ダイアログ
  openSelectFilesDialog(): void {
    const dialogRef = this.dialog.open(SelectUploadedFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        for (const row of result) {
          const lang = this.projectLanguages[0].lang_code;
          this.exhibit!.exhibit_files!.push({ file_id: row.id, lang_code: lang });
        }
        this.getFiles();
      }
    });
  }

  deleteFiles(i: number): void {
    this.exhibit!.exhibit_files!.splice(i, 1);
    this.getFiles();
  }

  //Youtube埋め込みコードダイアログ
  openYoutubeCodeDialog(): void {
    const dialogRef = this.dialog.open(MultiYoutubeDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { codes: this.exhibit!.youtube_codes }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibit!.youtube_codes = result;
        this.youtubeCodesControl.setValue(this.exhibit!.youtube_codes);
      }
    });
  }

  //外部リンク名称ダイアログ
  openExternallinkNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.exhibit!.externallink_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibit!.externallink_names = result;
        this.externallinkNamesControl.setValue(this.exhibit!.externallink_names);
      }
    });
  }

  //外部リンクＵＲＬダイアログ
  openExternallinkUrlDialog(): void {
    const dialogRef = this.dialog.open(MultiExternallinkDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { codes: this.exhibit!.externallink_urls }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibit!.externallink_urls = result;
        this.externallinkUrlsControl.setValue(this.exhibit!.externallink_urls);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // マップ検索用のキー制御
  onKeyDownFromSearchPlace(keyCode: number): boolean {
    if (keyCode && keyCode === 13) {
      // NOTE: Enterを叩かれると他の項目の編集ボタンが反応してしまうのを防ぐ
      return false;
    }
    return true;
  }

  // マップ検索
  searchMap(): void {
    const places = this.searchBox.getPlaces();
    if (places!.length === 0) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    places!.forEach(place => {
      if (!place.geometry || !place.geometry.location) {
        return;
      }
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    // console.log(bounds.getCenter().lat());
    // this.map.fitBounds(bounds);
    this.exhibitLatControl.setValue(this.round(bounds.getCenter().lat(), 8));
    this.exhibitLngControl.setValue(this.round(bounds.getCenter().lng(), 8));
    this.currentPosition = {
      lat: this.round(bounds.getCenter().lat(), 8),
      lng: this.round(bounds.getCenter().lng(), 8)
    }
    this.map.panTo(this.currentPosition);
  }

  // マップ検索フィールドクリア
  clearMapSearchField(): void {
    this.searchField.nativeElement.value = "";
  }

  // 展示に紐付く展示詳細情報設定
  private getExhibitDetails() {
    this.exhibitDetails = [];
    if (!!this.exhibit!.exhibit_detail_ids && this.exhibit!.exhibit_detail_ids.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトの展示情報を全て取得してから
      //       フロア情報に登録されている id で絞り込む。
      this.angularFirestore.collection<ExhibitDetail>(
        AppConst.FIRESTORE_EXHIBIT_DETAILS, ref => ref.where('project_id', '==', this.projectId)
        ).valueChanges({ idField: 'id' }).subscribe(docs => {
          this.exhibitDetails = [];
          if (!!docs) {
            for (const exhibitDetailId of this.exhibit!.exhibit_detail_ids!) {
              for (const exhibitDetail of docs) {
                if (exhibitDetailId === exhibitDetail.id) {
                  this.exhibitDetails.push(exhibitDetail);
                  break;
                }
              }
            }
          }
      });
    }
  }

  // 展示に紐付く予約設備情報設定
  private getReservationFacilities() {
    this.reservationFacilities = [];
    if (!!this.exhibit!.reservation_facility_ids && this.exhibit!.reservation_facility_ids.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトの展示情報を全て取得してから
      //       フロア情報に登録されている id で絞り込む。
      this.angularFirestore.collection<ReservationFacility>(
        AppConst.FIRESTORE_RESERVATION_FACILITIES, ref => ref.where('project_id', '==', this.projectId)
      ).valueChanges({ idField: 'id' }).subscribe(docs => {
        this.reservationFacilities = [];
        if (!!docs) {
          for (const reservationFacilityId of this.exhibit!.reservation_facility_ids!) {
            for (const reservationFacility of docs) {
              if (reservationFacilityId === reservationFacility.id) {
                this.reservationFacilities.push(reservationFacility);
                break;
              }
            }
          }
        }
      });
    }
  }

  // 展示詳細選択ダイアログ
  openSelectExhibitDetailsDialog(): void {
    const dialogRef = this.dialog.open(SelectExhibitDetailsDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { exhibit: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.exhibit!.exhibit_detail_ids) {
          this.exhibit!.exhibit_detail_ids = new Array<string>();
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!this.exhibit!.exhibit_detail_ids.includes(row.id)) {
            this.exhibit!.exhibit_detail_ids.push(row.id);
            isChanged = true;
          }
        }
        if (!!isChanged) {
          this.getExhibitDetails();
        }
      }
    });
  }

  // 予約設備選択ダイアログ
  openSelectReservationFacilitiesDialog(): void {
    const dialogRef = this.dialog.open(SelectReservationFacilitiesDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { exhibit: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.exhibit!.reservation_facility_ids) {
          this.exhibit!.reservation_facility_ids = new Array<string>();
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!this.exhibit!.reservation_facility_ids.includes(row.id)) {
            this.exhibit!.reservation_facility_ids.push(row.id);
            isChanged = true;
          }
        }
        if (!!isChanged) {
          this.getReservationFacilities();
        }
      }
    });
  }

  // 展示詳細リストの削除
  deleteExhibitDetail(id: string): void {
    if (!!this.exhibit!.exhibit_detail_ids!.includes(id)) {
      this.exhibit!.exhibit_detail_ids = this.exhibit!.exhibit_detail_ids!.filter(value => value !== id);
      this.getExhibitDetails();
    }
  }

  // 予約設備リストの削除
  deleteReservationFacility(id: string): void {
    if (!!this.exhibit!.reservation_facility_ids!.includes(id)) {
      this.exhibit!.reservation_facility_ids = this.exhibit!.reservation_facility_ids!.filter(value => value !== id);
      this.getReservationFacilities();
    }
  }
}
